import { useEffect, useState } from 'react'
// Curated Lucide registry — named imports are tree-shaken, so ONLY these icons
// land in the initial bundle. This replaces the previous
// `import * as Icons from 'lucide-react'` (used by the LucideIcon primitive and
// the builder IconPicker), which forced the ENTIRE ~1950-icon set into the main
// chunk because icons were resolved dynamically by name (`Icons[name]`),
// defeating tree-shaking.
//
// The registry covers every icon reached on a critical/common path: the home
// dashboard + its activity feed, the app chrome, the main menu, KPI cards, etc.
// Add an icon here when it must render instantly (no lazy flash) on such a path.
//
// Any name NOT in the registry (an editor-chosen builder icon, a future
// data-driven menu icon, an activity type added server-side) still works: the
// useLucideIcon hook lazy-loads it ONCE from the full Lucide set as a single
// on-demand chunk — never part of the initial download.
import {
  Activity, ArrowLeft, ArrowRight, BookOpen, Bug, CalendarDays, ChartColumn,
  ChevronDown, ChevronRight, ChevronUp, Circle, CircleCheck, CircleHelp, Clock, Columns3,
  Copy, Eye, FileText, Flag, Folder, Home, Kanban, Link, List, ListTodo, Menu,
  MessageSquare, Network, Pencil, Plus, RotateCcw, Search, Settings, SquareCheck,
  Star, Trash2, User, Users, X, Zap,
} from 'lucide-react'

export const ICON_REGISTRY = {
  Activity, ArrowLeft, ArrowRight, BookOpen, Bug, CalendarDays, ChartColumn,
  ChevronDown, ChevronRight, ChevronUp, Circle, CircleCheck, CircleHelp, Clock, Columns3,
  Copy, Eye, FileText, Flag, Folder, Home, Kanban, Link, List, ListTodo, Menu,
  MessageSquare, Network, Pencil, Plus, RotateCcw, Search, Settings, SquareCheck,
  Star, Trash2, User, Users, X, Zap,
}
// Back-compat aliases for names that older content/data may still use.
ICON_REGISTRY.HelpCircle = CircleHelp // renamed to CircleHelp in recent Lucide
ICON_REGISTRY.LinkIcon = Link

export const FALLBACK_ICON = CircleHelp

// Single shared promise: the full Lucide set is fetched at most once, on the
// first request for a non-registry icon, and reused for every later one.
let _fullModulePromise = null
const _lazyResolved = new Map() // name -> component | null (null = not a real icon)

function loadFullLucide() {
  if (!_fullModulePromise) _fullModulePromise = import('lucide-react')
  return _fullModulePromise
}

/**
 * Resolves a Lucide component by name without bundling the whole library.
 * Registry names resolve synchronously; any other name is lazy-loaded once from
 * the full set. The caller decides how to render the three states.
 *
 * @param {string} name PascalCase Lucide name (e.g. "ChevronRight").
 * @returns the icon component, `null` when the name is empty or not a real icon,
 *          or `undefined` while a lazy icon is still loading.
 */
export function useLucideIcon(name) {
  const registered = name ? ICON_REGISTRY[name] : null
  const [, force] = useState(0)

  useEffect(() => {
    if (!name || registered || _lazyResolved.has(name)) return
    let alive = true
    loadFullLucide().then((mod) => {
      _lazyResolved.set(name, mod[name] ?? null)
      if (alive) force((n) => n + 1)
    })
    return () => { alive = false }
  }, [name, registered])

  if (!name) return null
  if (registered) return registered
  if (_lazyResolved.has(name)) return _lazyResolved.get(name)
  return undefined
}

/**
 * Le SET LUCIDE COMPLET, chargé à la demande — la matière de la bibliothèque
 * d'icônes du builder.
 *
 * Renvoie la carte `nom → composant` des icônes CANONIQUES (~1700 ; les alias
 * dépréciés, eux, restent résolus un par un par `useLucideIcon`). Elle vient du
 * même import dynamique que le reste : appeler ceci ne met rien de plus dans le
 * téléchargement initial, et ne refait pas la requête si un lazy l'a déjà faite.
 *
 * Rendre 1700 icônes une par une avec `useLucideIcon` ferait 1700 effets et
 * autant de re-rendus pour une seule et même promesse : la grille lit donc
 * cette carte et rend `map[name]` de façon synchrone.
 */
export function loadLucideIcons() {
  return loadFullLucide().then((mod) => mod.icons)
}
